import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCategories, type ConsumerCategory } from '@/lib/api';
import { useLocale, useTk } from '@/lib/i18n';
import { showInlineAlert } from '@/lib/telegram';

const FALLBACK_ICONS: Record<string, string> = {
  food: '🍽️',
  hotel: '🏨',
  cafe: '☕',
  shopping: '🛍️',
  entertainment: '🎉',
  beauty: '💇',
  fitness: '💪',
  travel: '✈️',
  electronics: '📱',
  furniture: '🛋️',
  fashion: '👜',
  baby: '🧸',
};

interface RowItem {
  /** stable key for React */
  key: string;
  /** label rendered below the tile */
  label: string;
  /** emoji or asset character; future iteration can swap for `iconUrl` */
  icon: string;
  /** when set, tap navigates to this route; otherwise tap fires comingSoon toast */
  route?: string;
  /** when true, tile is rendered in disabled style */
  comingSoon: boolean;
}

/**
 * Koom GO 首页一级分类格子（取代旧 CategoryGrid）。
 *
 * 视觉规格（DESIGN.md §Components > Category Tile）：
 *   - 单 tile 56×56 rounded-2xl
 *   - active 背景 `brand.primary.500` `#FFC400`
 *   - comingSoon 背景 `neutral.100` + 50% opacity 图标
 *   - 标题在格子上方，与 design 草稿一致
 *
 * 用法：
 *   <CategoryRow kind="groupon" columns={5} />     // 接 getCategories() 真实数据
 *   <CategoryRow kind="used_market" columns={4} /> // 4 个前端 placeholder + comingSoon
 *
 * 关键约束：
 * - `kind="used_market"` 不打任何后端 API（A 口径）—— 4 个 tiles 来自前端常量。
 * - `comingSoon` tile 点击 → `showInlineAlert(tk('common.toast.coming_soon'))`，无副作用。
 */
export function CategoryRow({
  kind,
  columns,
}: {
  kind: 'groupon' | 'used_market';
  columns: 4 | 5;
}): React.ReactElement | null {
  const tk = useTk();
  const locale = useLocale();
  const [grouponItems, setGrouponItems] = useState<ConsumerCategory[] | null>(null);

  useEffect(() => {
    if (kind !== 'groupon') return;
    const ac = new AbortController();
    getCategories(ac.signal)
      .then((res) => setGrouponItems(res.items))
      .catch(() => setGrouponItems([]));
    return () => ac.abort();
  }, [kind]);

  const items: RowItem[] | null =
    kind === 'groupon'
      ? buildGrouponItems(grouponItems, locale, columns)
      : USED_MARKET_PLACEHOLDERS.map((p) => ({
          key: p.code,
          label: tk(p.labelKey),
          icon: FALLBACK_ICONS[p.code] ?? '🏷️',
          comingSoon: true,
        }));

  // 真实分类查询失败或为空 → 不渲染整段（No Mock Data）
  if (kind === 'groupon' && (items === null || items.length === 0)) return null;

  if (!items) return null;

  const sectionTitleKey =
    kind === 'groupon' ? 'nav.top.groupon' : 'used_market.section_title';

  return (
    <section
      aria-label={tk(sectionTitleKey)}
      className="space-y-3"
    >
      <h2 className="text-base font-semibold text-neutral-900">
        {tk(sectionTitleKey)}
      </h2>
      <div
        className={[
          'grid gap-3',
          columns === 5 ? 'grid-cols-5' : 'grid-cols-4',
        ].join(' ')}
      >
        {items.map((item) => (
          <Tile
            key={item.key}
            label={item.label}
            icon={item.icon}
            route={item.route}
            comingSoon={item.comingSoon}
            comingSoonMessage={tk('common.toast.coming_soon')}
          />
        ))}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: Tile
// ────────────────────────────────────────────────────────────────────────────

interface TileProps {
  label: string;
  icon: string;
  route?: string;
  comingSoon: boolean;
  comingSoonMessage: string;
}

function Tile({ label, icon, route, comingSoon, comingSoonMessage }: TileProps): React.ReactElement {
  const tileSurface = comingSoon
    ? 'bg-neutral-100 text-neutral-900/50'
    : 'bg-brand-primary-500 text-neutral-900';

  const tileInner = (
    <span
      aria-hidden
      className={[
        'flex aspect-square w-full items-center justify-center',
        'rounded-2xl text-2xl',
        tileSurface,
        'transition active:scale-[0.94]',
      ].join(' ')}
    >
      {icon}
    </span>
  );

  return (
    <div className="flex flex-col items-center gap-1.5">
      {comingSoon ? (
        <button
          type="button"
          onClick={() => showInlineAlert(comingSoonMessage)}
          className="block w-full"
        >
          {tileInner}
        </button>
      ) : route ? (
        <Link to={route} className="block w-full">
          {tileInner}
        </Link>
      ) : (
        <span className="block w-full">{tileInner}</span>
      )}
      <span
        className={[
          'text-[11px] leading-tight text-center truncate w-full',
          comingSoon ? 'text-neutral-400' : 'text-neutral-700',
        ].join(' ')}
      >
        {label}
      </span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function buildGrouponItems(
  raw: ConsumerCategory[] | null,
  locale: string,
  limit: number,
): RowItem[] | null {
  if (raw === null) return null;
  return raw
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .slice(0, limit)
    .map((c) => ({
      key: c.code,
      label: pickName(c, locale),
      icon: FALLBACK_ICONS[c.code] ?? '🏷️',
      route: c.comingSoon
        ? undefined
        : `/groupons?categoryCode=${encodeURIComponent(c.code)}`,
      comingSoon: c.comingSoon,
    }));
}

function pickName(c: ConsumerCategory, locale: string): string {
  if (locale === 'km-KH') return c.nameKm;
  if (locale === 'en') return c.nameEn;
  return c.nameZh;
}

const USED_MARKET_PLACEHOLDERS: ReadonlyArray<{ code: string; labelKey: string }> = [
  { code: 'electronics', labelKey: 'used_market.electronics' },
  { code: 'furniture',   labelKey: 'used_market.furniture' },
  { code: 'fashion',     labelKey: 'used_market.fashion' },
  { code: 'baby',        labelKey: 'used_market.baby' },
];
